import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Tutorial } from "@/assets";
import { numeroParaExtenso } from "@/utils/numeroParaExtenso";

interface MoneyFieldProps {
  value: number | string;
  onChange: (value: string) => void;
  hasErrors?: boolean;
  required?: boolean;
  label: string;
  description?: string;
  videoUrl?: string;
  activeStep?: number;
  totalSteps?: number;
}

export const MoneyField: React.FC<MoneyFieldProps> = ({
  value,
  onChange,
  label,
  description,
  hasErrors,
  required,
  videoUrl,
  activeStep = 1,
  totalSteps = 25
}) => {
  const isInvalid = hasErrors && required && !value && value !== 0;
  const [extenso, setExtenso] = useState("");

  // Atualiza o valor por extenso quando o componente monta ou o valor muda
  useEffect(() => {
    if (value && value !== "") {
      try {
        // Remove todos os separadores e substitui vírgula por ponto para calcular o valor numérico
        const valorStr = String(value).replace(/\./g, "").replace(",", ".");
        const valorNumerico = parseFloat(valorStr);
        if (!isNaN(valorNumerico)) {
          setExtenso(numeroParaExtenso(valorNumerico));
        } else {
          setExtenso("");
        }
      } catch (e) {
        setExtenso("");
      }
    } else {
      setExtenso("");
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Implementação da máscara de moeda
    let valor = e.target.value.replace(/\D/g, "");
    
    // Se o valor for vazio, não queremos exibir nada
    if (valor === "") {
      onChange("");
      return;
    }
    
    // Formatação com separadores de milhar e decimais
    valor = (parseInt(valor) / 100).toFixed(2);
    
    // Converte para o formato brasileiro (1.234,56)
    valor = valor.replace(".", ",");
    
    // Adiciona os separadores de milhar
    if (valor.length > 6) {
      valor = valor.replace(/(\d)(\d{3}),(\d{2})$/, "$1.$2,$3");
    }
    
    // Para valores maiores, continua adicionando os separadores de milhar
    if (valor.length > 10) {
      valor = valor.replace(/(\d)(\d{3})\.(\d{3}),(\d{2})$/, "$1.$2.$3,$4");
    }
    
    if (valor.length > 14) {
      valor = valor.replace(/(\d)(\d{3})\.(\d{3})\.(\d{3}),(\d{2})$/, "$1.$2.$3.$4,$5");
    }
    
    onChange(valor);
  };

  return (
    <>
      <h1 className="text-background font-bold text-sm sm:text-2xl mb-2.5 sm:mb-4">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </h1>
      {description && (
        <h2 className="text-grey-light text-sm sm:text-base mb-6 sm:mb-8">{description}</h2>
      )}
      
      <p className="text-grey-light text-xs sm:text-sm mb-4 italic">
        Digite o valor em reais
      </p>
      
      <div className="flex flex-col justify-center text-xs sm:text-sm gap-4 w-full">
        <div className="relative">
          <span className="absolute left-0 top-1/2 -translate-y-1/2 text-background/70">R$</span>
          <Input
            type="text"
            value={value}
            onChange={handleChange}
            className="pl-6 placeholder:text-background/60 border-0 border-b border-b-background rounded-none focus-visible:ring-0"
            placeholder="0,00"
          />
        </div>
        
        <p className="text-grey-light text-xs sm:text-sm italic text-center">
          {extenso ? `(${extenso})` : ""}
        </p>
        
        {isInvalid && (
          <div className="text-red-500 text-sm mt-2">Este campo é obrigatório</div>
        )}
      </div>
      
      {videoUrl && (
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="ghost" className="flex gap-2 items-center bg-background/10 rounded-md w-fit px-4 py-2 mt-4">
              <Tutorial fill="var(--background)" />
              <span>Ver vídeo explicativo</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="aspect-video min-w-2/3 bg-black/90 rounded-2xl border border-gray-800 text-grey-light">
            <div style={{ left: 0, width: '100%', height: 0, position: 'relative', paddingBottom: '56.25%' }}>
              <iframe
                src={videoUrl}
                style={{ top: 0, left: 0, width: '100%', height: '100%', position: 'absolute', border: 0 }}
                allowFullScreen
                scrolling="no"
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share;"
              >
              </iframe>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
